import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_URL_V4, environment } from 'src/environments/environment.local';
import {
  ApiHttpResponse,
  Coordinate,
  GpsOnly,
  Top,
  TrainingSection,
} from '@kinomap-dev/ng-ui-lib';
import { Comment } from '@kinomap-dev/ng-ui-lib/lib/models/comment.model';

@Injectable({
  providedIn: 'root',
})
export class GpsOnlyService {
  private http: HttpClient = inject(HttpClient);

  getInformation(gpsOnlyId: number): Observable<GpsOnly> {
    const url: string = environment.api.gpsOnly.informations.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );
    return this.http.get<ApiHttpResponse<GpsOnly>>(url).pipe(
      map((httpResponse: ApiHttpResponse<GpsOnly>) => {
        return httpResponse.data;
      })
    );
  }

  getTrainingSections(gpsOnlyId: number): Observable<TrainingSection[]> {
    const url: string = `${environment.api.gpsOnly.trainingSections.replace('{gpsOnlyId}', gpsOnlyId.toString())}`;
    return this.http.get<ApiHttpResponse<TrainingSection[]>>(url).pipe(
      map((httpResponse: ApiHttpResponse<TrainingSection[]>) => {
        return httpResponse.data;
      })
    );
  }

  getCoordinates(gpsOnlyId: number): Observable<Coordinate[]> {
    const url: string = `${environment.api.gpsOnly.coordinates.replace('{gpsOnlyId}', gpsOnlyId.toString())}`;
    return this.http.get<ApiHttpResponse<Coordinate[]>>(url).pipe(
      map((httpResponse: ApiHttpResponse<Coordinate[]>) => {
        return httpResponse.data;
      })
    );
  }

  // --- 💖 Likes

  getAllLikes(
    gpsOnlyId: string,
    page: number = 1,
    limit: number = 15
  ): Observable<any> {
    const url: string = environment.api.gpsOnly.likes.replace(
      '{gpsOnlyId}',
      gpsOnlyId
    );
    const param: HttpParams = new HttpParams()
      .set('page', page)
      .set('limit', limit);
    return this.http.get<any>(url, { params: param });
  }

  postLike(gpsOnlyId: number): Observable<any> {
    const url: string = environment.api.gpsOnly.likes.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );
    return this.http
      .post<ApiHttpResponse<any>>(url, {})
      .pipe(map((res: ApiHttpResponse<any>) => res.data));
  }

  deleteLike(gpsOnlyId: number) {
    const url: string = environment.api.gpsOnly.likes.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );
    return this.http
      .delete<ApiHttpResponse<any>>(url)
      .pipe(map((res: ApiHttpResponse<any>) => res.data));
  }

  // --- 💬 Comments

  getComments(gpsOnlyId: number): Observable<Comment[]> {
    const url: string = environment.api.gpsOnly.comments.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );
    return this.http.get<any>(url).pipe(
      map((httpResponse: any) => {
        return httpResponse.data;
      })
    );
  }

  postComment(
    gpsOnlyId: string,
    commentBody: string,
    rootCommentId: string | null = null
  ): Observable<any> {
    const url: string = environment.api.gpsOnly.comments.replace(
      '{gpsOnlyId}',
      gpsOnlyId
    );
    const param: HttpParams = new HttpParams().set(
      'root_comment_id',
      rootCommentId
    );
    const body: { body: string } = { body: commentBody };
    return this.http.post<any>(url, body, { params: param });
  }

  deleteComment(gpsOnlyId: string, commentId: string): Observable<any> {
    const url: string = `${environment.api.gpsOnly.comments.replace('{gpsOnlyId}', gpsOnlyId)}/${commentId}`;
    return this.http.delete<any>(url);
  }

  // 🎖️ Ranking

  /**
   * Retourne l'esemble des participant au classement d'un Gps-Only
   * @param gpsOnlyId
   * @param page
   * @param ageMin
   * @param ageMax
   * @param gender
   * @param machineType
   * @param manufacturer
   */
  getRanking(
    gpsOnlyId: number,
    page: number,
    ageMin: number,
    ageMax: number,
    gender: string,
    machineType: string,
    manufacturer: string
  ): Observable<ApiHttpResponse<Top[]>> {
    const url: string = environment.api.gpsOnly.ranking.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );

    let params: HttpParams = new HttpParams().set('page', page);
    if (ageMax !== null && ageMin !== null)
      params = params.set('ageMin', ageMin).set('ageMax', ageMax);
    if (gender) params = params.set('sex', gender);
    if (machineType) params = params.set('equipment_type', machineType);
    if (manufacturer) params = params.set('manufacturer', manufacturer);

    return this.http.get<ApiHttpResponse<Top[]>>(url, { params });
  }

  /**
   * Retourne une liste de GpsOnly qui sont recommandés.
   * @param limit :number = 10
   * @returns : GpsOnly[]
   */
  getRecommended(limit: number = 10): Observable<GpsOnly[]> {
    const url: string = environment.api.gpsOnly.recommended;
    const params: HttpParams = new HttpParams().set('limit', limit);
    return this.http.get<GpsOnly[]>(url, { params });
  }

  /**
   * Retourne une liste des GpsOnly mis en avant cette semaine.
   * @param sport : 'cycling' | 'running' | null = null
   * @returns : GpsOnly[]
   */
  getWeekly(sport: 'cycling' | 'running' | null = null): Observable<GpsOnly[]> {
    const url: string = environment.api.gpsOnly.weekly;
    const params: HttpParams = new HttpParams().set('sport', sport);
    return this.http.get<ApiHttpResponse<GpsOnly[]>>(url, { params }).pipe(
      map((result: ApiHttpResponse<GpsOnly[]>) => {
        return result.data;
      })
    );
  }

  getMapmodeGPX(gpsOnlyId: number): Observable<Blob> {
    const url: string = `${API_URL_V4}/mapmode/${gpsOnlyId}/gpxFile`;
    const httpOptions: {
      responseType: 'json';
    } = { responseType: 'blob' as 'json' };
    return this.http.get<Blob>(url, httpOptions);
  }
}
